import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { THEME_NAMES } from '../types.js';
import { useTheme } from '../hooks/useTheme.js';

export function ThemeDropdown(): ReactElement {
  const { theme: current, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function onDocClick(ev: MouseEvent): void {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(ev.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function select(name: (typeof THEME_NAMES)[number]): void {
    setTheme(name);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-label="Theme"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex min-h-[48px] min-w-[48px] items-center justify-center rounded-md px-2"
        style={{ color: 'var(--text-main)' }}
      >
        <span aria-hidden="true">🎨</span>
      </button>
      {open ? (
        <ul
          role="listbox"
          aria-label="Themes"
          className="absolute right-0 mt-2 w-44 overflow-hidden rounded-md border shadow-lg"
          style={{
            background: 'var(--bg-surface)',
            borderColor: 'var(--border)',
            color: 'var(--text-main)',
          }}
        >
          {THEME_NAMES.map((name) => (
            <li key={name}>
              <button
                type="button"
                role="option"
                aria-selected={current === name}
                onClick={() => select(name)}
                className="block w-full px-3 py-2 text-left text-sm"
                style={{
                  color: 'var(--text-main)',
                  background:
                    current === name ? 'var(--bg-primary)' : 'transparent',
                }}
              >
                {name}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}